import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, firestore } from './firebase';
import { setUserData } from './redux/userSlice';
import { doc, getDoc } from 'firebase/firestore';
import UserForm from './components/UserForm';
import ChatInterface from './components/ChatInterface';
import LandingPage from './components/LandingPage';
import Profile from './components/Profile';
import PricingPage from './components/PricingPage';
import Authentication from './components/Authentication';
import './App.css';
import Loader from './components/Loader'; 
import About from './components/About';
import ReturnAndRefund from './components/ReturnAndRefund';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import FeedbackPage from './components/FeedbackPage';
import Horoscope from './components/Horoscope';
import LifeReport from './components/LifeReportLandingPage';
import VarshaphalReport from './components/VarshphalReportLandingPage';
import ReportForm from './components/ReportForm';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { ToastContainer } from "react-toastify";
import ScrollToTop from './components/ScrollToTop';
import "react-toastify/dist/ReactToastify.css";
import VoiceCallInterface from './components/VoiceCallInterface';
import MiniReportsPage from './components/MiniReportsPage';
import ReportDetailPage from './components/ReportDetailPage';
// import TrackingStatus from './components/TrackingStatus';

const GA_MEASUREMENT_ID = 'G-EHKNT1X608';

const getPlatform = () => {
  if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
    return 'PWA';
  }
  return 'Website';
};

const App = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const [loadingData, setLoadingData] = useState(true);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  
  useEffect(() => {
    ReactGA.initialize(GA_MEASUREMENT_ID);
    ReactGA.send("pageview"); // Sends initial page view
  }, []);
  
  useEffect(() => {
    const checkUserProfile = async (uid) => {
      try {
        const userRef = doc(firestore, 'users', uid);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          dispatch(setUserData(docSnap.data()));
        } else {
          dispatch(setUserData(null));
        }
      } catch (error) {
        console.error("Error fetching user data: ", error);
        dispatch(setUserData(null));
      } finally {
        setLoadingData(false);
      }
    };

    if (user) {
      checkUserProfile(user.uid);
    } else {
      setLoadingData(false); // No user, so we're done loading data
    }
  }, [user, dispatch]);

  if (loadingAuth || loadingData) {
    return <Loader />; 
  }

  // Private route no longer requires authentication check for /chat
  // const PrivateRoute = ({ children }) => {
  //   if (!userData || !userData.dob || !userData.time || !userData.location) {
  //     return <Navigate to="/form" />;
  //   }
  //   return children;
  // };

  // FormRoute only checks userData fields
  const FormRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/" />;
    }
    if (userData && userData.dob && userData.time && userData.location) {
      return <Navigate to="/chat?login=complete" />;
    }
    return children;
  };
  const AuthRoute = ({ children }) => {
    if (user) {
      // Check if user data contains necessary fields; if not, redirect to the appropriate page
      if (userData && userData.dob && userData.time && userData.location) {
        return <Navigate to="/chat?login=complete" replace />;
      } else {
        return <Navigate to="/form?login=complete" replace />;
      }
    }
  
    // If no user is logged in, render the children (e.g., Authentication component)
    return children;
  };
  // const trackingRef = "AAAP138006001";
  // const statusUpdates = [
  //   { date: "Jan 08", time: "10:54 am", status: "Delivered" },
  //   { date: "Jan 05", time: "16:00 pm", status: "Final Collection Reminder" },
  //   { date: "Jan 03", time: "12:12 pm", status: "Ready For Collection" },
  // ];
  return (
    <Router>
      <GA4PageTracker />
      <ToastContainer />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/authentication" element={<AuthRoute><Authentication /></AuthRoute>} />
        <Route path="/form" element={<FormRoute><UserForm /></FormRoute>} />
        <Route path="/chat" element={<ChatInterface />} />
        <Route path="/voice-call" element={<VoiceCallInterface />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/about" element={<About />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/mini-reports" element={<MiniReportsPage />} />
        <Route path="/mini-report" element={<ReportDetailPage />} />
        <Route path="/report/life-report" element={<LifeReport />} />
        <Route path="/report/varshaphal-report" element={<VarshaphalReport />} />
        <Route path="/report/:reportName/payment-page" element={<ReportForm />} />
        {/* <Route path="/report/status" element={<TrackingStatus trackingRef={trackingRef} statusUpdates={statusUpdates} />} /> */}
        <Route path="/horoscope/:sign/:date/:type" element={<Horoscope />} />
        <Route path="/returns" element={<ReturnAndRefund />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/tnc" element={<TermsAndConditions />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
};

// Page Tracker Component to Handle Route Changes
const GA4PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
    });
  }, [location]);

  return null; // This component doesn't render anything
};

export default App;
